import { createForm, type RenderParams } from '@seek/forms-ui';
import { rules } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Button,
  Stack,
  Text,
  Textarea,
  useResponsiveValue,
} from 'braid-design-system';
import React, { useCallback } from 'react';

import { onSubmitFail } from 'src/components/Card/onSubmitFail';
import type { DirtyFormProps } from 'src/components/FormDrawer/FormDrawer';
import { useOnFormDirtyChange } from 'src/components/FormDrawer/useOnFormDirtyChange';
import { SUMMARY } from 'src/components/Router';
import { Link } from 'src/components/Router/Link';
import { useEnterReadMode } from 'src/components/Router/hooks';
import { READ } from 'src/consts';
import { useAppConfig } from 'src/modules/appConfig';
import { useFocus } from 'src/modules/hooks/useFocus';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import { SUMMARY_FORM } from 'src/reducers/forms';

import { useSummary, useUpdateSummary } from '../data';

import translations from './.vocab';

interface MappedProps {
  dirty: boolean;
  currentSummary: string;
}

interface FormValues {
  summary: string;
}

const mapFormStateToProps = ({
  form: { dirty },
  values: { summary: currentSummary },
}: {
  form: { dirty: boolean };
  values: { summary: string };
}): MappedProps => ({
  dirty,
  currentSummary,
});

type FormProps = RenderParams<FormValues> &
  ReturnType<typeof mapFormStateToProps>;

const { FormProvider, useField } = createForm<FormValues, MappedProps>(
  SUMMARY_FORM,
  {
    formType: 'braid',
  },
);

const summaryRules = rules.personalSummary();

const EditSummaryForm = ({
  dirty,
  handleSubmit,
  onFormDirtyChange,
}: FormProps & DirtyFormProps) => {
  const { t } = useTranslations(translations);
  const enterReadMode = useEnterReadMode('summary');

  useOnFormDirtyChange({ onFormDirtyChange, dirty });

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false,
  });

  const summaryRef = useFocus<HTMLTextAreaElement>({
    card: SUMMARY,
    fieldName: 'summary',
  });

  const [updateSummary] = useUpdateSummary();

  const submitForm = useCallback(
    ({ summary }: { summary: string }) => {
      if (dirty) {
        updateSummary({ careerObjectivesData: { personalStatement: summary } });
      }
      enterReadMode();
    },
    [dirty, enterReadMode, updateSummary],
  );

  const summaryField = useField({
    id: 'summary',
    ...summaryRules,
  });

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <Stack space="medium">
        <Stack space="small">
          <Textarea
            id="summary"
            label={t('Summary')}
            characterLimit={rules.personalSummary().validatorProps.max}
            description={t(
              'Highlight your unique experiences, ambitions and strengths.',
            )}
            ref={summaryRef}
            data={{
              automation: 'summary-input',
              'hj-masked': 'true',
            }}
            lines={6}
            lineLimit={isMobile ? 10 : 16}
            reserveMessageSpace
            {...summaryField}
            value={summaryField.value || ''}
            highlightRanges={summaryField.highlightRanges || undefined}
          />
          <Text tone="secondary">
            {t(
              'Stay safe. Don’t include sensitive personal information such as...',
            )}
          </Text>
        </Stack>
        <Actions>
          <Button
            type="submit"
            tone="formAccent"
            data={{ automation: 'summary-save-button' }}
          >
            {t('Save')}
          </Button>
          <Link
            to={SUMMARY}
            mode={READ}
            data={{ automation: 'summary-cancel-link' }}
            variant="transparent"
            tone="formAccent"
            component={Button}
          >
            {t('Cancel')}
          </Link>
        </Actions>
      </Stack>
    </form>
  );
};

export const EditSummary = (dirtyFormProps: DirtyFormProps) => {
  const { summary } = useSummary();
  const page = useTypedSelector((store) => store.router.page);
  const { language } = useAppConfig();
  return (
    <FormProvider
      mapFormStateToProps={mapFormStateToProps}
      initialValues={{ summary }}
      onSubmitFail={(errors) => {
        onSubmitFail(errors, page);
      }}
      language={language}
    >
      {(formProps) => <EditSummaryForm {...dirtyFormProps} {...formProps} />}
    </FormProvider>
  );
};
